import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ComposerPrimitive, MessagePrimitive, ThreadPrimitive } from '@assistant-ui/react';
import {
  ChevronDown,
  History,
  PanelRightOpen,
  Plus,
  Send,
  Sparkles,
  Square,
  X,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router';

import { Button } from '@accio3d/design-system/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@accio3d/design-system/collapsible';
import { Tooltip, TooltipContent, TooltipTrigger } from '@accio3d/design-system/tooltip';
import { cn } from '@accio3d/design-system/utils';
import { ArcessioBadge } from '@accio3d/shared-ui/components/custom/branding';

import type { AgentSuggestion, AgentSuggestionDisposition } from '../../api/agent';
import { useApiServices } from '../../api/services';
import { useAuth } from '../../hooks/useAuth';
import { useAgentSidebar, useAgentThread } from '../../hooks/useAgentSidebar';
import { useScreenContext } from '../../hooks/useScreenContext';
import { AgentConversationList } from './agent-conversation-list';
import { AgentError } from './agent-error';
import { AgentMarkdownText } from './agent-markdown';
// import { AgentMemoryView } from './agent-memory-view';
import { AgentPendingQuestion, useHasPendingQuestion } from './agent-pending-question';
import { AgentProposedMemory } from './agent-proposed-memory';
import { AgentRuntimeProvider, useTurnTokens } from './agent-runtime';
import { AgentWorking } from './agent-working';
import {
  isDurable,
  setSuggestionsCollapsed,
  suggestionId,
  useAgentSuggestions,
  useDismissSuggestion,
} from './suggestion-store';
import { resolveSuggestionRoute } from './execute-action';

function landsOnCurrentScreen(
  route: string | null,
  location: { pathname: string; search: string },
) {
  if (!route) return false;

  const [pathname, search = ''] = route.split('?');
  if (pathname !== location.pathname) return false;

  const current = new URLSearchParams(location.search);
  for (const [key, value] of new URLSearchParams(search)) {
    if (current.get(key) !== value) return false;
  }
  return true;
}

function SuggestionCards({ threadId }: { threadId: string | undefined }) {
  const { suggestions: proposed, collapsed } = useAgentSuggestions();
  const dismiss = useDismissSuggestion();
  const { agent } = useApiServices();
  const navigate = useNavigate();
  const location = useLocation();

  const suggestions = proposed.filter(
    (suggestion) => !landsOnCurrentScreen(resolveSuggestionRoute(suggestion), location),
  );

  if (suggestions.length === 0) return null;

  // Hides a suggestion and records why, so it does not come back on the next load.
  const settle = (suggestion: AgentSuggestion, disposition: AgentSuggestionDisposition) => {
    dismiss(suggestion);
    if (!threadId || !isDurable(suggestion)) return;
    void agent
      .setSuggestionState(threadId, suggestion.fingerprint!, disposition)
      .catch(() => undefined);
  };

  // Navigates while keeping the agent's own query params.
  const navigatePreservingPanel = (route: string) => {
    const [pathname, search = ''] = route.split('?');
    const merged = new URLSearchParams(search);

    for (const [key, value] of new URLSearchParams(location.search)) {
      if (key.startsWith('agent-') && !merged.has(key)) merged.set(key, value);
    }

    const query = merged.toString();
    void navigate(query ? `${pathname}?${query}` : pathname);
  };

  return (
    <Collapsible
      open={!collapsed}
      onOpenChange={(open) => setSuggestionsCollapsed(!open)}
      className="border-t border-border/50 px-3 py-2"
    >
      <CollapsibleTrigger className="flex w-full items-center gap-1.5 rounded-sm py-0.5 text-left text-xs font-medium text-muted-foreground hover:text-foreground">
        <Sparkles className="h-3 w-3 shrink-0" />
        <span className="flex-1 truncate">
          {collapsed
            ? `${suggestions.length} suggested next step${suggestions.length === 1 ? '' : 's'}`
            : 'Suggested next steps'}
        </span>
        <ChevronDown
          className={cn('h-3.5 w-3.5 shrink-0 transition-transform', !collapsed && 'rotate-180')}
        />
      </CollapsibleTrigger>

      <CollapsibleContent className="mt-1 space-y-0.5">
        {suggestions.map((suggestion) => {
          const route = resolveSuggestionRoute(suggestion);
          return (
            <div key={suggestionId(suggestion)} className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  {route ? (
                    <button
                      type="button"
                      className="min-w-0 flex-1 truncate rounded-sm px-1.5 py-1 text-left text-xs hover:bg-muted/60"
                      onClick={() => {
                        settle(suggestion, 'accepted');
                        navigatePreservingPanel(route);
                      }}
                    >
                      {suggestion.title}
                    </button>
                  ) : (
                    <ThreadPrimitive.Suggestion
                      prompt={suggestion.title}
                      send
                      title={suggestion.rationale}
                      className="min-w-0 flex-1 truncate rounded-sm px-1.5 py-1 text-left text-xs hover:bg-muted/60 disabled:opacity-50"
                      onClick={() => settle(suggestion, 'accepted')}
                    >
                      {suggestion.title}
                    </ThreadPrimitive.Suggestion>
                  )}
                </TooltipTrigger>
                <TooltipContent side="left" className="max-w-56 text-xs">
                  {suggestion.rationale}
                </TooltipContent>
              </Tooltip>

              <Button
                size="icon"
                variant="ghost"
                title="Dismiss this suggestion"
                aria-label={`Dismiss: ${suggestion.title}`}
                className="h-6 w-6 shrink-0 text-muted-foreground"
                onClick={() => settle(suggestion, 'dismissed')}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          );
        })}
      </CollapsibleContent>
    </Collapsible>
  );
}

function EmptyState() {
  const screen = useScreenContext();
  const { agent } = useApiServices();
  const { organization } = useAuth();

  const { data: prompts } = useQuery({
    queryKey: ['agent-prompts', organization?.id, screen?.path],
    queryFn: () => agent.suggestedPrompts(screen!.path),
    enabled: !!screen && !!organization?.id,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <div className="flex h-full flex-col gap-3 p-4">
      <div className="rounded-r-lg border-l-2 border-primary bg-muted/30 p-3">
        <ArcessioBadge variant="solid" />
        <p className="mt-3 text-sm leading-relaxed text-foreground">
          Ask about what you are looking at. I can read your data, compare options and suggest what
          to do next.
        </p>
      </div>

      <div className="mt-auto flex flex-col items-start gap-2">
        {(prompts ?? []).map((prompt) => (
          <ThreadPrimitive.Suggestion
            key={prompt}
            prompt={prompt}
            send
            className="rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-left text-[13px] text-foreground transition-colors hover:bg-primary/10 disabled:opacity-50"
          >
            {prompt}
          </ThreadPrimitive.Suggestion>
        ))}
      </div>
    </div>
  );
}

function Messages() {
  return (
    <MessagePrimitive.Root className="px-3 py-2">
      <MessagePrimitive.If user>
        <div className="ml-auto max-w-[85%] rounded-lg bg-primary/10 px-3 py-2 text-sm">
          <MessagePrimitive.Parts />
        </div>
      </MessagePrimitive.If>
      <MessagePrimitive.If assistant>
        <div className="max-w-[95%] text-sm leading-relaxed">
          <MessagePrimitive.Parts components={{ Text: AgentMarkdownText }} />
        </div>
      </MessagePrimitive.If>
    </MessagePrimitive.Root>
  );
}

type PanelView = 'thread' | 'conversations'; // | 'memory'; (no memory for now)

function PanelBody() {
  const { threadId, setThreadId } = useAgentThread();
  const hasPendingQuestion = useHasPendingQuestion();
  const tokens = useTurnTokens();
  const { setOpen } = useAgentSidebar();
  const [view, setView] = useState<PanelView>('thread');

  // if (view === 'memory') return <AgentMemoryView onClose={() => setView('thread')} />;
  if (view === 'conversations') {
    return <AgentConversationList onSelect={() => setView('thread')} />;
  }

  return (
    <div className="flex h-full flex-col">
      <header className="arcessio-ai-header flex items-center gap-2 border-b border-border/50 px-3 py-2 pt-3">
        <span className="text-[15px] font-semibold text-foreground">Ask Arcessio</span>
        <div className="ml-auto flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            title="New conversation"
            onClick={() => setThreadId(undefined)}
          >
            <Plus className="h-4 w-4" />
          </Button>
          {/* <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            title="What I remember"
            onClick={() => setView('memory')}
          >
            <Brain className="h-4 w-4" />
          </Button> */}
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            title="Conversations"
            onClick={() => setView('conversations')}
          >
            <History className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            title="Close"
            onClick={() => setOpen(false)}
          >
            <PanelRightOpen className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <ThreadPrimitive.Root className="flex min-h-0 flex-1 flex-col">
        <ThreadPrimitive.Viewport
          className="min-h-0 flex-1 overflow-y-auto"
          aria-live="polite"
          aria-atomic="false"
        >
          <ThreadPrimitive.Empty>
            <EmptyState />
          </ThreadPrimitive.Empty>
          <ThreadPrimitive.Messages components={{ Message: Messages }} />
        </ThreadPrimitive.Viewport>

        <AgentWorking tokens={tokens} />

        <AgentError />

        <AgentProposedMemory />
        <SuggestionCards threadId={threadId} />

        <AgentPendingQuestion />

        {!hasPendingQuestion && (
          <ComposerPrimitive.Root className="flex items-end gap-2 border-t border-border/50 p-2">
            <ComposerPrimitive.Input
              rows={1}
              placeholder="Ask Arcessio…"
              data-agent-autofocus=""
              className="max-h-32 min-h-9 flex-1 resize-none rounded-md border border-border/60 bg-background px-2 py-1.5 text-sm outline-none focus-visible:ring-1 focus-visible:ring-primary"
            />

            <ThreadPrimitive.If running={false}>
              <ComposerPrimitive.Send asChild>
                <Button size="icon" className="btn-arcessio h-9 w-9 rounded-lg" title="Send">
                  <Send className="h-4 w-4" />
                </Button>
              </ComposerPrimitive.Send>
            </ThreadPrimitive.If>
            <ThreadPrimitive.If running>
              <Button size="icon" variant="secondary" className="h-9 w-9" disabled title="Working…">
                <Square className="h-4 w-4" />
              </Button>
            </ThreadPrimitive.If>
          </ComposerPrimitive.Root>
        )}
      </ThreadPrimitive.Root>
    </div>
  );
}

export function AgentPanel() {
  const { open } = useAgentSidebar();
  const { organization } = useAuth();
  const [mounted, setMounted] = useState(false);
  const panelRef = useRef<HTMLElement>(null);
  const returnFocusTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) setMounted(true);
  }, [open]);

  useEffect(() => {
    if (open) {
      if (!mounted) return;
      const active = document.activeElement;

      returnFocusTo.current =
        active instanceof HTMLElement && active !== document.body ? active : null;
      panelRef.current?.querySelector<HTMLElement>('[data-agent-autofocus]')?.focus();
      return;
    }

    const opener = returnFocusTo.current;
    returnFocusTo.current = null;

    if (opener?.isConnected) opener.focus();
  }, [open, mounted]);

  if (!organization) return null;
  return (
    <aside
      ref={panelRef}
      aria-hidden={!open}
      inert={!open}
      className={cn(
        'h-full border-r border-border/50 bg-background',
        open ? 'w-full' : 'hidden',
        open && 'fixed inset-0 z-40 md:static md:inset-auto',
      )}
    >
      {mounted && (
        <AgentRuntimeProvider>
          <PanelBody />
        </AgentRuntimeProvider>
      )}
    </aside>
  );
}
